import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Plus, Trash2, Bell, Settings2, Loader2, Power, Send, RefreshCw, History, Clock, PackageCheck, CalendarClock, Satellite, CheckCheck, AlertTriangle, Truck, Building2, Activity, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';
import { Switch } from './ui/switch';
import { TransportHistoryTab } from './TransportHistoryTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const ORANGE = '#ee7203';
const COMPLETE_ALL_CONFIRM_PHRASE = 'COMPLETE ALL';

const SEND_TIME_OPTIONS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
];

const BRANCH_COLORS = {
  C30: { border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  E70: { border: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' },
};

// Compact "how long ago" for the geofence diagnostics panel — the job runs
// every 60s, so anything under a couple of minutes reads as fresh/healthy;
// older than that is the signal the job has actually stopped running.
const timeAgo = (isoString) => {
  if (!isoString) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(isoString).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
};

// Signed version for a scheduled NEXT run time — "in 42s" if still upcoming,
// "OVERDUE by 3m" if APScheduler's own next_run_time has already passed
// without firing (the clearest possible sign the interval trigger has
// stopped advancing even though the job is still technically registered).
const timeUntil = (isoString) => {
  if (!isoString) return null;
  const seconds = Math.round((new Date(isoString).getTime() - Date.now()) / 1000);
  if (seconds >= 0) {
    if (seconds < 60) return `in ${seconds}s`;
    return `in ${Math.round(seconds / 60)}m`;
  }
  const overdue = Math.abs(seconds);
  if (overdue < 60) return `OVERDUE by ${overdue}s`;
  return `OVERDUE by ${Math.round(overdue / 60)}m`;
};

export const TransportSettingsTab = () => {
  const [subTab, setSubTab] = useState('rules');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [newCustomer, setNewCustomer] = useState({ customer_name: '', postcodes: '', emails: '' });
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [sendingNowId, setSendingNowId] = useState(null);
  const [sendNowResult, setSendNowResult] = useState(null);
  const [showCompleteAllModal, setShowCompleteAllModal] = useState(false);
  const [completeAllTyped, setCompleteAllTyped] = useState('');
  const [completingAll, setCompletingAll] = useState(false);
  const [refreshHistoricalOpenId, setRefreshHistoricalOpenId] = useState(null);
  const [refreshHistoricalDates, setRefreshHistoricalDates] = useState({});
  const [refreshingHistoricalId, setRefreshingHistoricalId] = useState(null);

  // Samsara vehicle-branch mapping summary — every Samsara vehicle grouped by
  // which branch's registry it's mapped to, so you don't have to open every
  // vehicle card on both branch tabs to see what's linked where.
  const [samsaraSummary, setSamsaraSummary] = useState(null);
  const [loadingSamsaraSummary, setLoadingSamsaraSummary] = useState(true);
  const [samsaraSummaryError, setSamsaraSummaryError] = useState(null);

  const loadSamsaraSummary = () => {
    setLoadingSamsaraSummary(true);
    setSamsaraSummaryError(null);
    api.get(`${API}/admin/integrations/samsara/branch-summary`)
      .then(res => setSamsaraSummary(res.data))
      .catch(err => setSamsaraSummaryError(err?.response?.data?.detail || 'Failed to load Samsara vehicle summary — check the API key in Settings > Integrations > Samsara.'))
      .finally(() => setLoadingSamsaraSummary(false));
  };

  // Geofence job diagnostics — snapshot of the last 60s auto-complete cycle
  // (services/samsara_geofence.py), so "is it actually running, and what did
  // it see" is answered by opening this page, no server logs needed.
  const [geofenceDiag, setGeofenceDiag] = useState(null);
  const [loadingGeofenceDiag, setLoadingGeofenceDiag] = useState(true);
  const [geofenceDiagError, setGeofenceDiagError] = useState(null);

  const loadGeofenceDiagnostics = () => {
    setLoadingGeofenceDiag(true);
    setGeofenceDiagError(null);
    api.get(`${API}/admin/integrations/samsara/geofence-diagnostics`)
      .then(res => setGeofenceDiag(res.data))
      .catch(err => setGeofenceDiagError(err?.response?.data?.detail || 'Failed to load geofence diagnostics.'))
      .finally(() => setLoadingGeofenceDiag(false));
  };

  // Manually fires ONE geofence-check cycle right now, bypassing the scheduler
  // and leader election entirely — same underlying job function, called
  // directly. Tells us whether the job body itself works even if the
  // scheduled/automatic version never seems to run.
  const [runningGeofenceNow, setRunningGeofenceNow] = useState(false);
  const runGeofenceNow = () => {
    setRunningGeofenceNow(true);
    setGeofenceDiagError(null);
    api.post(`${API}/admin/integrations/samsara/geofence-run-now`)
      .then(res => { setGeofenceDiag(res.data); toast.success('Geofence check ran — see updated status below'); })
      .catch(err => { setGeofenceDiagError(err?.response?.data?.detail || 'Failed to run geofence check.'); toast.error('Manual geofence run failed — see error below'); })
      .finally(() => setRunningGeofenceNow(false));
  };

  useEffect(() => {
    api.get(`${API}/transport-planning/settings`).then(res => setSettings(res.data)).catch(() => {});
    loadNotifications();
    loadSamsaraSummary();
    loadGeofenceDiagnostics();
  }, []);

  const loadNotifications = () => {
    setLoadingNotifications(true);
    api.get(`${API}/transport-planning/customer-notifications`)
      .then(res => setNotifications(res.data))
      .catch(() => {})
      .finally(() => setLoadingNotifications(false));
  };

  const saveSettings = async (changes) => {
    setSaving(true);
    try {
      const res = await api.put(`${API}/transport-planning/settings`, changes);
      setSettings(res.data);
      toast.success('Transport settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addCustomer = async () => {
    if (!newCustomer.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setAddingCustomer(true);
    try {
      await api.post(`${API}/transport-planning/customer-notifications`, {
        customer_name: newCustomer.customer_name.trim(),
        postcodes: newCustomer.postcodes.split(',').map(p => p.trim().toUpperCase()).filter(Boolean),
        emails: newCustomer.emails.split(',').map(e => e.trim()).filter(Boolean),
      });
      setNewCustomer({ customer_name: '', postcodes: '', emails: '' });
      toast.success('Customer notification added');
      loadNotifications();
    } catch {
      toast.error('Failed to add customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const deleteCustomer = async (id) => {
    try {
      await api.delete(`${API}/transport-planning/customer-notifications/${id}`);
      toast.success('Removed');
      loadNotifications();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const testSendCustomer = async (id) => {
    setTestingId(id);
    try {
      const res = await api.post(`${API}/transport-planning/customer-notifications/${id}/test-send`);
      const { order_number, delivery_date, run_slot, sent_to } = res.data;
      toast.success(`Test email sent to ${sent_to.join(', ')} — using order ${order_number} (${delivery_date}, ${run_slot})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestingId(null);
    }
  };

  const refreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.post(`${API}/transport-planning/refresh-all`);
      const { added, already_present, excluded_direct_or_collection } = res.data;
      toast.success(
        `Refresh complete — ${added} order(s) added to their Unplanned Queue (Collections flagged, not excluded), ${already_present} already on Transport, ${excluded_direct_or_collection} excluded (Direct deliveries only).`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to refresh');
    } finally {
      setRefreshingAll(false);
    }
  };

  const sendNow = async (n) => {
    setSendingNowId(n.id);
    try {
      const res = await api.post(`${API}/transport-planning/customer-notifications/${n.id}/send-now`);
      setSendNowResult({ customer_name: n.customer_name, ...res.data });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingNowId(null);
    }
  };

  const refreshHistorical = async (n) => {
    const range = refreshHistoricalDates[n.id] || {};
    if (!range.from || !range.to) {
      toast.error('Pick both a From date and a To date');
      return;
    }
    setRefreshingHistoricalId(n.id);
    try {
      const res = await api.post(`${API}/transport-planning/customer-notifications/${n.id}/refresh-historical`, {
        date_from: range.from, date_to: range.to,
      });
      const { orders_found, cleared_count } = res.data;
      toast.success(
        orders_found > 0
          ? `Reset ${cleared_count} order${cleared_count === 1 ? '' : 's'} for ${n.customer_name} — click "Send Now" to re-send them.`
          : `No orders found for ${n.customer_name} in that date range.`
      );
      setRefreshHistoricalOpenId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to refresh historical notifications');
    } finally {
      setRefreshingHistoricalId(null);
    }
  };

  const completeAllToday = async () => {
    setCompletingAll(true);
    try {
      const res = await api.post(`${API}/transport-planning/complete-all-today`);
      const { updated_count, protected_count } = res.data;
      toast.success(
        `Marked ${updated_count} pending entr${updated_count === 1 ? 'y' : 'ies'} as complete for today. ` +
        `${protected_count} entr${protected_count === 1 ? 'y' : 'ies'} with an existing status were left untouched.`
      );
      setShowCompleteAllModal(false);
      setCompleteAllTyped('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to complete all');
    } finally {
      setCompletingAll(false);
    }
  };

  if (!settings) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2" data-testid="transport-settings-subtabs">
        <button
          data-testid="transport-settings-rules-subtab"
          onClick={() => setSubTab('rules')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${subTab === 'rules' ? 'text-white shadow' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={subTab === 'rules' ? { backgroundColor: ORANGE } : {}}
        >
          <Settings2 className="w-4 h-4" /> Planning Rules
        </button>
        <button
          data-testid="transport-settings-notifications-subtab"
          onClick={() => setSubTab('notifications')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${subTab === 'notifications' ? 'text-white shadow' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={subTab === 'notifications' ? { backgroundColor: ORANGE } : {}}
        >
          <Bell className="w-4 h-4" /> Customer Notifications
        </button>
        <button
          data-testid="transport-settings-history-subtab"
          onClick={() => setSubTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all ${subTab === 'history' ? 'text-white shadow' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
          style={subTab === 'history' ? { backgroundColor: ORANGE } : {}}
        >
          <History className="w-4 h-4" /> History
        </button>
      </div>

      {subTab === 'history' && <TransportHistoryTab />}

      {subTab === 'rules' && (
        <div className="space-y-4">
        <Card className={settings.automation_enabled === false ? 'border-red-300' : 'border-green-300'}>
          <CardContent className="pt-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${settings.automation_enabled === false ? 'bg-red-100' : 'bg-green-100'}`}>
                <Power className={`w-5 h-5 ${settings.automation_enabled === false ? 'text-red-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">Transport Automation</p>
                <p className="text-xs text-gray-500">
                  {settings.automation_enabled === false
                    ? 'OFF — parsed orders will NOT be auto-planned or added to the Unplanned Queue automatically. Use this while testing/developing order parsing.'
                    : 'ON — parsed customer orders are automatically planned onto the board or added to the Unplanned Queue.'}
                </p>
              </div>
            </div>
            <Switch
              data-testid="transport-automation-toggle"
              checked={settings.automation_enabled !== false}
              onCheckedChange={(checked) => saveSettings({ automation_enabled: checked })}
            />
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100">
                <RefreshCw className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">Refresh All</p>
                <p className="text-xs text-gray-500">
                  Sweeps every parsed order with a future delivery date. Anything already on the board or in an
                  Unplanned Queue is left untouched — nothing missing gets added to its date's Unplanned Queue.
                </p>
              </div>
            </div>
            <Button
              data-testid="transport-refresh-all-btn"
              onClick={refreshAll}
              disabled={refreshingAll}
              className="gap-2 flex-shrink-0"
              style={{ backgroundColor: '#2563eb', color: 'white' }}
            >
              {refreshingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {refreshingAll ? 'Refreshing…' : 'Refresh All'}
            </Button>
          </CardContent>
        </Card>
        <Card className="border-amber-300">
          <CardContent className="pt-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 flex-shrink-0">
                <CheckCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-bold text-sm text-gray-800">Complete All (Today)</p>
                <p className="text-xs text-gray-500">
                  Marks every still-pending entry across every vehicle/run, both branches, for TODAY as complete.
                  Anything already given ANY other status (complete, failed, collection, etc) is left untouched — protected.
                </p>
              </div>
            </div>
            <Button
              data-testid="complete-all-today-btn"
              onClick={() => setShowCompleteAllModal(true)}
              className="gap-2 flex-shrink-0"
              style={{ backgroundColor: '#d97706', color: 'white' }}
            >
              <CheckCheck className="w-4 h-4" /> Complete All
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Auto-Planning Rules</CardTitle>
            <CardDescription>
              Controls how parsed customer orders are automatically slotted onto vehicles/runs, or left in the Unplanned Queue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>Max Drive-Time Threshold (hours)</Label>
                <Input
                  data-testid="drive-hours-threshold-input"
                  type="number" step="0.5" min="1" max="14"
                  defaultValue={settings.drive_hours_threshold}
                  onBlur={(e) => saveSettings({ drive_hours_threshold: parseFloat(e.target.value) || 7 })}
                />
                <p className="text-xs text-gray-500">Total run time (driving + offload) beyond which a new drop is refused and tried on a different vehicle.</p>
              </div>
              <div className="space-y-2">
                <Label>Offload Time Per Drop (minutes)</Label>
                <Input
                  data-testid="offload-minutes-input"
                  type="number" step="5" min="0" max="180"
                  defaultValue={settings.offload_minutes}
                  onBlur={(e) => saveSettings({ offload_minutes: parseFloat(e.target.value) || 30 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Working Day Start</Label>
                <Input
                  data-testid="work-start-input"
                  type="time"
                  defaultValue={settings.work_start}
                  onBlur={(e) => saveSettings({ work_start: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Working Day End</Label>
                <Input
                  data-testid="work-end-input"
                  type="time"
                  defaultValue={settings.work_end}
                  onBlur={(e) => saveSettings({ work_end: e.target.value })}
                />
                <p className="text-xs text-gray-500">Run 1 is treated as the AM slot, any later run as PM — used on the customer delivery-notification banner.</p>
              </div>
              <div className="space-y-2">
                <Label>Default Active Vehicle Count</Label>
                <Input
                  data-testid="fleet-limit-input"
                  type="number" min="1" max="20"
                  placeholder="All on-road vehicles"
                  defaultValue={settings.default_active_vehicle_count || ''}
                  onBlur={(e) => saveSettings({ default_active_vehicle_count: e.target.value ? parseInt(e.target.value) : null })}
                />
                <p className="text-xs text-gray-500">Leave blank to allow auto-planning onto every on-road vehicle. Set a number to cap it (e.g. only plan onto the first 4).</p>
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t">
              <Label>Moffet Detection Keywords</Label>
              <Input
                data-testid="moffet-keywords-input"
                defaultValue={(settings.moffet_keywords || []).join(', ')}
                onBlur={(e) => saveSettings({ moffet_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              />
              <p className="text-xs text-gray-500">Comma-separated. A confident match on any of these words in the ticket text requires a Moffet-flagged vehicle.</p>
            </div>
            <div className="space-y-2">
              <Label>Ambiguous Access Signals (triggers AI check)</Label>
              <Input
                data-testid="ambiguous-keywords-input"
                defaultValue={(settings.ambiguous_signal_keywords || []).join(', ')}
                onBlur={(e) => saveSettings({ ambiguous_signal_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              />
              <p className="text-xs text-gray-500">When one of these vaguer phrases appears (but no clear Moffet keyword), an AI check reads the ticket to decide if a Moffet is really needed — keeps cost low by skipping AI on clear-cut cases.</p>
            </div>
            <div className="space-y-2">
              <Label>External Carrier Keywords (Out of Area)</Label>
              <Input
                data-testid="external-carrier-keywords-input"
                defaultValue={(settings.external_carrier_keywords || []).join(', ')}
                onBlur={(e) => saveSettings({ external_carrier_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              />
              <p className="text-xs text-gray-500">Comma-separated. If any product line's code or description contains one of these (e.g. the "91ACCEXTCARRIAGE" external-carriage charge), the order is held in the Unplanned Queue with an "OUT OF AREA" tag and is never auto-planned onto our fleet.</p>
            </div>
            {saving && <p className="text-xs text-gray-400">Saving…</p>}
          </CardContent>
        </Card>

        <Card className="border-sky-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Satellite className="w-5 h-5 text-sky-600" /> Samsara Geofence Detection</CardTitle>
            <CardDescription>
              Controls the live-tracking "arrived at drop" auto-detection on today's Transport map (see Settings →
              Integrations → Samsara for the API key, and Settings → Vehicles for each vehicle's Samsara mapping).
              These need real-world tuning against an actual drop before being trusted — adjust here any time, no redeploy needed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>Geofence Arrival Radius (miles)</Label>
                <Input
                  data-testid="geofence-radius-input"
                  type="number" step="0.05" min="0.05" max="2"
                  defaultValue={settings.geofence_radius_miles}
                  onBlur={(e) => saveSettings({ geofence_radius_miles: parseFloat(e.target.value) || 0.4 })}
                />
                <p className="text-xs text-gray-500">How close (in miles) a vehicle must get to a stop's postcode to count as "arrived" — a postcode's geocoded centre can genuinely sit 0.3–0.5mi from where a vehicle actually parks, so keep this generous; the separate "must be stopped" check (not just nearby) already filters out drive-bys.</p>
              </div>
              <div className="space-y-2">
                <Label>Geofence Dwell Time (minutes)</Label>
                <Input
                  data-testid="geofence-dwell-input"
                  type="number" step="0.5" min="0.5" max="30"
                  defaultValue={settings.geofence_dwell_minutes}
                  onBlur={(e) => saveSettings({ geofence_dwell_minutes: parseFloat(e.target.value) || 3 })}
                />
                <p className="text-xs text-gray-500">How long a vehicle must stay within the radius before leaving counts as a completed drop (filters out drive-bys).</p>
              </div>
            </div>

            <div className="pt-2 border-t space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
                  <Activity className="w-3.5 h-3.5 text-sky-600" /> Live Job Status
                </span>
                <Button
                  variant="outline" size="sm" onClick={loadGeofenceDiagnostics} disabled={loadingGeofenceDiag}
                  data-testid="geofence-diagnostics-refresh-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingGeofenceDiag ? 'animate-spin' : ''}`} /> Check Status
                </Button>
                <Button
                  variant="outline" size="sm" onClick={runGeofenceNow} disabled={runningGeofenceNow}
                  className="ml-2"
                  data-testid="geofence-run-now-btn"
                  title="Runs one real geofence check cycle immediately, bypassing the scheduler — can genuinely mark drops complete if a vehicle has moved past its geofence + dwell time"
                >
                  {runningGeofenceNow ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Satellite className="w-3.5 h-3.5 mr-1.5" />}
                  Run Now
                </Button>
              </div>
              <p className="text-[11px] text-gray-400 -mt-2">
                What the geofence auto-complete job (checks every 60s) saw and did on its last cycle — no server logs needed.
              </p>

              {loadingGeofenceDiag ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
                </div>
              ) : geofenceDiagError ? (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="geofence-diagnostics-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {geofenceDiagError}
                </div>
              ) : !geofenceDiag?.diagnostics ? (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="geofence-diagnostics-never-run">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">The geofence job hasn't completed a single cycle yet on whichever server is currently the scheduler leader.</p>
                    <p className="text-xs mt-1 text-amber-700">
                      This instance {geofenceDiag?.is_leader_on_this_instance ? 'IS' : 'is NOT'} the scheduler leader right now
                      {geofenceDiag?.is_leader_on_this_instance
                        ? ' — so if this stays empty after another "Check Status" in a minute, something is preventing the job body from running or completing.'
                        : ' — the job runs on a different server replica, which is normal for a fresh deploy but worth a re-check shortly.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3" data-testid="geofence-diagnostics-panel">
                  <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg border text-xs">
                    <span className="flex items-center gap-1.5 font-semibold text-gray-700">
                      <Clock className="w-3.5 h-3.5" /> Last run: {timeAgo(geofenceDiag.diagnostics.last_run_at) || 'unknown'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {geofenceDiag.is_leader_on_this_instance ? (
                        <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> This server is the scheduler leader</>
                      ) : (
                        <><XCircle className="w-3.5 h-3.5 text-gray-400" /> This server is NOT the scheduler leader</>
                      )}
                    </span>
                    <span className="text-gray-500">
                      Live Samsara positions fetched: <strong className="text-gray-800">{geofenceDiag.diagnostics.live_locations_count ?? 'n/a'}</strong>
                    </span>
                  </div>

                  {geofenceDiag.diagnostics.live_locations_error && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="geofence-diagnostics-live-error">
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      Couldn't fetch live Samsara positions this cycle: {geofenceDiag.diagnostics.live_locations_error}
                    </div>
                  )}

                  {(() => {
                    const sched = geofenceDiag.scheduler_live_status;
                    if (!sched) return null;
                    const overdue = sched.next_run_time && new Date(sched.next_run_time).getTime() < Date.now();
                    const healthy = sched.scheduler_reference_registered && sched.scheduler_running && sched.job_found && !overdue;
                    return (
                      <div
                        className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border text-xs ${healthy ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                        data-testid="geofence-scheduler-live-status"
                      >
                        <span className="flex items-center gap-1.5 font-semibold text-gray-700">
                          {healthy ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
                          Scheduler registration (APScheduler's own view, not our job-cycle log above)
                        </span>
                        <span className="text-gray-600">
                          Reference registered: <strong>{sched.scheduler_reference_registered ? 'yes' : 'NO'}</strong>
                        </span>
                        <span className="text-gray-600">
                          Scheduler running: <strong>{sched.scheduler_running === null ? 'n/a' : sched.scheduler_running ? 'yes' : 'NO'}</strong>
                        </span>
                        <span className="text-gray-600">
                          Job found: <strong>{sched.job_found ? 'yes' : 'NO'}</strong>
                        </span>
                        {sched.job_found && (
                          <span className={overdue ? 'text-red-700 font-semibold' : 'text-gray-600'}>
                            Next run: <strong>{timeUntil(sched.next_run_time) || 'unknown'}</strong>
                          </span>
                        )}
                        {!sched.scheduler_reference_registered && (
                          <p className="w-full text-red-700 mt-1">
                            The startup code that creates this scheduler never reached the point where it registers itself here —
                            it either threw an exception before <code>.start()</code>, or never ran at all. See Startup Registration Log below.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  {geofenceDiag.startup_status && (
                    <div
                      className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${geofenceDiag.startup_status.registered === false ? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                      data-testid="geofence-startup-status"
                    >
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold">
                          Startup registration log — {geofenceDiag.startup_status.registered === false ? 'FAILED' : 'succeeded'}
                          {geofenceDiag.startup_status.started_at && ` at ${new Date(geofenceDiag.startup_status.started_at).toLocaleString('en-GB')}`}
                          {geofenceDiag.startup_status.failed_at && ` at ${new Date(geofenceDiag.startup_status.failed_at).toLocaleString('en-GB')}`}
                        </p>
                        {geofenceDiag.startup_status.error && (
                          <p className="text-xs mt-1 break-words font-mono">{geofenceDiag.startup_status.error}</p>
                        )}
                        {geofenceDiag.startup_status.traceback && (
                          <pre className="text-[10px] mt-2 whitespace-pre-wrap break-words bg-white border rounded p-2 max-h-40 overflow-y-auto">{geofenceDiag.startup_status.traceback}</pre>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {['C30', 'E70'].map((branchId) => {
                      const b = geofenceDiag.diagnostics.branches?.[branchId];
                      const colors = BRANCH_COLORS[branchId];
                      return (
                        <div key={branchId} className={`rounded-xl border-2 ${colors.border} p-3`} data-testid={`geofence-diagnostics-${branchId}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded font-bold ${colors.badge}`}>{branchId}</span>
                          </div>
                          {!b ? (
                            <p className="text-xs text-gray-400">Not checked this cycle.</p>
                          ) : b.error ? (
                            <div className="flex items-start gap-1.5 text-xs text-red-700">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              {b.error}
                            </div>
                          ) : (
                            <table className="w-full text-xs text-gray-600">
                              <tbody>
                                <tr><td className="py-0.5">Vehicles mapped to Samsara</td><td className="py-0.5 text-right font-semibold text-gray-800">{b.mapped_vehicles}</td></tr>
                                <tr><td className="py-0.5">Vehicles with a live GPS signal</td><td className="py-0.5 text-right font-semibold text-gray-800">{b.vehicles_with_live_signal}</td></tr>
                                <tr><td className="py-0.5">Pending entries checked</td><td className="py-0.5 text-right font-semibold text-gray-800">{b.entries_checked}</td></tr>
                                <tr><td className="py-0.5">Entries auto-completed this cycle</td><td className="py-0.5 text-right font-semibold text-gray-800">{b.entries_updated}</td></tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-sky-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-sky-600" /> Samsara Vehicles by Branch
              </span>
              <Button
                variant="outline" size="sm" onClick={loadSamsaraSummary} disabled={loadingSamsaraSummary}
                data-testid="samsara-branch-summary-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loadingSamsaraSummary ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </CardTitle>
            <CardDescription>
              Every Samsara vehicle at a glance, grouped by which branch's registry it's mapped to (Settings → Vehicles
              → Samsara Vehicle ID, per branch) — so you don't have to open every vehicle card to see what's linked where.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingSamsaraSummary ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
              </div>
            ) : samsaraSummaryError ? (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {samsaraSummaryError}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['C30', 'E70'].map((branchId) => {
                    const vehicles = samsaraSummary?.branches?.[branchId] || [];
                    const colors = BRANCH_COLORS[branchId];
                    return (
                      <div key={branchId} className={`rounded-xl border-2 ${colors.border} p-3`} data-testid={`samsara-branch-summary-${branchId}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded font-bold ${colors.badge}`}>{branchId}</span>
                          <span className="text-xs text-gray-500">{vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'} mapped</span>
                        </div>
                        {vehicles.length === 0 ? (
                          <p className="text-xs text-gray-400 py-2">No Samsara vehicles mapped for {branchId} yet.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {vehicles.map((v, i) => (
                              <div key={i} className="flex items-center justify-between gap-2 text-xs bg-white rounded-lg border px-2.5 py-1.5">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <Truck className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                  <span className="font-semibold text-gray-800 truncate">{v.vehicle_registration || 'Unnamed vehicle'}</span>
                                  {v.driver_name && <span className="text-gray-400 truncate">— {v.driver_name}</span>}
                                </div>
                                {v.samsara_vehicle_found ? (
                                  <span className="text-sky-600 flex-shrink-0 truncate max-w-[40%]">{v.samsara_license_plate || v.samsara_name}</span>
                                ) : (
                                  <span className="text-red-500 flex-shrink-0 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> not found</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="pt-2 border-t">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-xs font-semibold text-gray-600">All Samsara tags in use (raw)</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mb-2">
                    Samsara has no separate "branch" field — only free-text Tags. This lists every tag actually
                    applied to a vehicle in your Samsara org, so you can see whether it's using real depot names
                    (e.g. Nottingham, Chorley) instead of C30/E70.
                  </p>
                  {(samsaraSummary?.all_tags || []).length === 0 ? (
                    <p className="text-xs text-gray-400">No tags found on any vehicle in Samsara — this fleet isn't tagged at all yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="samsara-all-tags-list">
                      {samsaraSummary.all_tags.map((t) => (
                        <span key={t.name} className="text-xs bg-sky-50 border border-sky-200 text-sky-800 rounded-full px-2.5 py-1">
                          {t.name} <span className="text-sky-400">×{t.vehicle_count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-2 border-t space-y-3">
                  {(() => {
                    const byBranch = samsaraSummary?.unmapped_by_branch || { C30: [], E70: [] };
                    const noTag = samsaraSummary?.unmapped_no_tag || [];
                    const totalUnmapped = (byBranch.C30?.length || 0) + (byBranch.E70?.length || 0) + noTag.length;
                    return (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-600">Unmapped Samsara vehicles</span>
                          <span className="text-xs text-gray-400">({totalUnmapped} of {samsaraSummary?.total_samsara_vehicles ?? 0} total)</span>
                        </div>
                        {totalUnmapped === 0 ? (
                          <p className="text-xs text-gray-400">Every Samsara vehicle is mapped to a branch — nothing orphaned.</p>
                        ) : (
                          <>
                            {['C30', 'E70'].map((branchId) => (
                              (byBranch[branchId] || []).length > 0 && (
                                <div key={branchId} data-testid={`samsara-unmapped-${branchId}`}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${BRANCH_COLORS[branchId].badge}`}>{branchId} tag</span>
                                    <span className="text-xs text-gray-400">not yet linked to a {branchId} registry vehicle</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {byBranch[branchId].map((v) => (
                                      <span key={v.id} className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2.5 py-1">
                                        {v.license_plate || v.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )
                            ))}
                            {noTag.length > 0 && (
                              <div data-testid="samsara-unmapped-no-tag">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-gray-100 text-gray-600">No C30/E70 tag</span>
                                  <span className="text-xs text-gray-400">see "All Samsara tags" above for this vehicle's real tag(s)</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {noTag.map((v) => (
                                    <span key={v.id} className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded-full px-2.5 py-1" title={v.tags?.length ? `Tags: ${v.tags.join(', ')}` : 'No tags set in Samsara'}>
                                      {v.license_plate || v.name}
                                      {v.tags?.length > 0 && <span className="text-gray-400"> ({v.tags.join(', ')})</span>}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border-green-200">
          <CardContent className="pt-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100 flex-shrink-0">
              <Power className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-bold text-sm text-gray-800">New Transport UI</p>
              <p className="text-xs text-gray-500">
                Live for everyone — the staged rollout has finished, and the old classic board is fully retired.
                There's no per-user toggle anymore, so nobody can get stuck on an old view.
              </p>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {subTab === 'notifications' && (
        <Card>
          <CardHeader>
            <CardTitle>Customer Delivery Notifications</CardTitle>
            <CardDescription>
              Every day at the scheduled time below, we check all planned deliveries for these customers/postcodes
              and email ONE consolidated summary per customer — grouped into a section for each upcoming delivery
              date, listing every order (with product/quantity) for that day. Only sent when at least one order is
              new or has genuinely changed/moved/been confirmed since their last email — nothing is re-sent if
              nothing has changed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <Clock className="w-4 h-4 flex-shrink-0" style={{ color: ORANGE }} />
              <Label className="text-xs font-semibold text-gray-700 whitespace-nowrap">Daily send time</Label>
              <select
                data-testid="notification-send-time-select"
                className="text-sm border rounded-md px-2 py-1.5 bg-white"
                value={settings.customer_notification_send_time || '17:00'}
                onChange={(e) => saveSettings({ customer_notification_send_time: e.target.value })}
              >
                {SEND_TIME_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <span className="text-xs text-gray-500">Europe/London — defaults to 5:00 PM (17:00)</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-1 md:col-span-1">
                <Label className="text-xs">Customer Name</Label>
                <Input data-testid="new-customer-name-input" value={newCustomer.customer_name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, customer_name: e.target.value })}
                  placeholder="e.g. MKM Bromsgrove" />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label className="text-xs">Postcode(s) — comma separated</Label>
                <Input data-testid="new-customer-postcodes-input" value={newCustomer.postcodes}
                  onChange={(e) => setNewCustomer({ ...newCustomer, postcodes: e.target.value })}
                  placeholder="B61 0AB, B61 0AC" />
              </div>
              <div className="space-y-1 md:col-span-1">
                <Label className="text-xs">Email(s) — comma separated</Label>
                <Input data-testid="new-customer-emails-input" value={newCustomer.emails}
                  onChange={(e) => setNewCustomer({ ...newCustomer, emails: e.target.value })}
                  placeholder="ops@customer.com" />
              </div>
              <Button data-testid="add-customer-notification-btn" onClick={addCustomer} disabled={addingCustomer}
                className="gap-2" style={{ backgroundColor: ORANGE, color: 'white' }}>
                <Plus className="w-4 h-4" /> Add
              </Button>
            </div>

            {loadingNotifications ? (
              <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">No customer notifications configured yet.</div>
            ) : (
              <div className="space-y-2" data-testid="customer-notifications-list">
                {notifications.map((n) => (
                  <div key={n.id} className="border rounded-lg bg-white">
                  <div className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{n.customer_name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(n.postcodes || []).map((p, i) => <Badge key={i} variant="outline" className="text-xs">{p}</Badge>)}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{(n.emails || []).join(', ')}</p>
                    </div>
                    <div className="flex items-center flex-shrink-0">
                      <Button
                        data-testid={`send-now-customer-notification-${n.id}`}
                        variant="outline" size="sm" onClick={() => sendNow(n)}
                        disabled={sendingNowId === n.id}
                        className="gap-1.5 text-xs mr-1"
                        style={{ borderColor: ORANGE, color: ORANGE }}
                      >
                        {sendingNowId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
                        Send Now
                      </Button>
                      <Button
                        data-testid={`refresh-historical-toggle-${n.id}`}
                        variant="outline" size="sm"
                        onClick={() => setRefreshHistoricalOpenId(refreshHistoricalOpenId === n.id ? null : n.id)}
                        className="gap-1.5 text-xs mr-1"
                      >
                        <History className="w-3.5 h-3.5" />
                        Refresh Historical
                      </Button>
                      <Button
                        data-testid={`test-send-customer-notification-${n.id}`}
                        variant="outline" size="sm" onClick={() => testSendCustomer(n.id)}
                        disabled={testingId === n.id}
                        className="gap-1.5 text-xs mr-1"
                      >
                        {testingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Test
                      </Button>
                      <Button
                        data-testid={`delete-customer-notification-${n.id}`}
                        variant="ghost" size="sm" onClick={() => deleteCustomer(n.id)}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {refreshHistoricalOpenId === n.id && (
                    <div className="flex flex-wrap items-end gap-3 px-3 pb-3 pt-1 border-t bg-gray-50" data-testid={`refresh-historical-panel-${n.id}`}>
                      <p className="text-xs text-gray-500 w-full -mb-1">
                        Pick a date range — every one of {n.customer_name}'s deliveries in that range will be treated as
                        "never notified", so the next "Send Now" click re-sends them.
                      </p>
                      <div className="space-y-1">
                        <Label className="text-xs">From</Label>
                        <Input
                          type="date" data-testid={`refresh-historical-from-${n.id}`}
                          className="h-8 text-xs"
                          value={refreshHistoricalDates[n.id]?.from || ''}
                          onChange={(e) => setRefreshHistoricalDates((prev) => ({ ...prev, [n.id]: { ...prev[n.id], from: e.target.value } }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">To</Label>
                        <Input
                          type="date" data-testid={`refresh-historical-to-${n.id}`}
                          className="h-8 text-xs"
                          value={refreshHistoricalDates[n.id]?.to || ''}
                          onChange={(e) => setRefreshHistoricalDates((prev) => ({ ...prev, [n.id]: { ...prev[n.id], to: e.target.value } }))}
                        />
                      </div>
                      <Button
                        data-testid={`refresh-historical-submit-${n.id}`}
                        size="sm" onClick={() => refreshHistorical(n)}
                        disabled={refreshingHistoricalId === n.id}
                        className="gap-1.5 text-xs"
                        style={{ backgroundColor: ORANGE, color: 'white' }}
                      >
                        {refreshingHistoricalId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        Reset this range
                      </Button>
                    </div>
                  )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!sendNowResult} onOpenChange={(open) => !open && setSendNowResult(null)}>
        <DialogContent data-testid="send-now-result-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5" style={{ color: ORANGE }} />
              {sendNowResult?.customer_name}
            </DialogTitle>
            <DialogDescription>
              {sendNowResult?.sent
                ? `1 consolidated email sent just now, covering ${sendNowResult.included_count} order${sendNowResult.included_count === 1 ? '' : 's'} across their upcoming delivery dates.`
                : sendNowResult?.error
                  ? 'Send failed — see the error below. Nothing was marked as delivered, so this will retry automatically next time.'
                  : "Nothing to send — this customer's delivery schedule hasn't changed since their last email."}
            </DialogDescription>
          </DialogHeader>
          {sendNowResult?.error && (
            <div className="p-2.5 border border-red-200 rounded-lg bg-red-50 text-sm" data-testid="send-now-result-error">
              <p className="text-xs text-red-600 break-words">{sendNowResult.error}</p>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="send-now-result-close-btn" onClick={() => setSendNowResult(null)} style={{ backgroundColor: ORANGE, color: 'white' }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showCompleteAllModal} onOpenChange={(open) => { if (!completingAll) { setShowCompleteAllModal(open); if (!open) setCompleteAllTyped(''); } }}>
        <DialogContent data-testid="complete-all-today-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> Complete All (Today)?
            </DialogTitle>
            <DialogDescription>
              This marks every still-pending entry — across every vehicle, every run, both branches — for TODAY
              as complete. Entries that already have any other status (complete, failed, collection, etc) are
              protected and will not be touched. This cannot be undone in bulk.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Type <span className="font-mono font-bold">{COMPLETE_ALL_CONFIRM_PHRASE}</span> to confirm</Label>
            <Input
              data-testid="complete-all-today-confirm-input"
              value={completeAllTyped}
              onChange={(e) => setCompleteAllTyped(e.target.value)}
              placeholder={COMPLETE_ALL_CONFIRM_PHRASE}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              data-testid="complete-all-today-cancel-btn"
              variant="outline"
              onClick={() => { setShowCompleteAllModal(false); setCompleteAllTyped(''); }}
              disabled={completingAll}
            >
              Cancel
            </Button>
            <Button
              data-testid="complete-all-today-confirm-btn"
              onClick={completeAllToday}
              disabled={completeAllTyped.trim().toUpperCase() !== COMPLETE_ALL_CONFIRM_PHRASE || completingAll}
              className="gap-2"
              style={{ backgroundColor: '#d97706', color: 'white' }}
            >
              {completingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
              {completingAll ? 'Completing…' : 'Confirm & Complete All'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
